import { useEffect, useState } from "react";
import {
	BorderColors,
	BorderRadius,
	Button,
	ButtonSize,
	Dropdown,
	Flex,
	FontFamilies,
	ForegroundColors,
	TextColors,
	Typography,
} from "@abel-technologies-as/components";
import { Container } from "./style";
import { DhcsTable, DeleteDhcModal } from "@/modules/dhcs";
import { ApprovePayoutModal, Filters, GlobalExportLoader } from "@/shared/components";
import { useCachedFilter, useCachedPage } from "@/shared/hooks";
import { filters } from "./constants";
import { useExportDhcs, useGetDhcList, useGetDhcs } from "@/modules/dhcs/hooks";
import { Dhc } from "@/modules/dhcs/types";
import { useTranslation } from "react-i18next";
import { parseNumber } from "@/shared/utils";
import { PermissionGuard } from "@/modules/authentication/components/permission-guard/PermissionGuard";
import { getEnvVar } from "@/config/environment";
import { PermissionEnum } from "@/modules/authentication/types";
import { ApprovePayout, Export } from "@/shared/icons";
import styled from "styled-components";
import { DhcProfile } from "@/shared/types";

const ExportItem = styled.li`
	cursor: pointer;
	padding: 8px 12px;
	width: 100%;
	&:hover {
		background-color: #f9fafb;
	}
`;

const Content = () => {
	const { t } = useTranslation("dhc");
	const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
	const { t: tCommon } = useTranslation("common");
	const [selectedRows, setSelectedRows] = useState<Array<number | string>>([]);
	const [clearSelection, setClearSelection] = useState<boolean>(false);
	const [dhcsInfo, setDhcsInfo] = useState<DhcProfile[]>([]);

	const [openApprovePayoutModal, setOpenApprovePayoutModal] = useState<boolean>(false);
	const { filterValues, setFilterValue, clearFilter, clearAllFilters } = useCachedFilter("dhcs-filters");
	const [page, setPage] = useCachedPage("dhcs-table");
	const status = typeof filterValues.status === "string" ? filterValues.status.toLowerCase() : "";
	const name = typeof filterValues.name === "string" ? filterValues.name : "";
	const keywords = typeof filterValues.keywords === "string" ? filterValues.keywords : "";
	const [openExportDropdown, setOpenExportDropdown] = useState<boolean>(false);
	const { exportFormat, handleExport, handleCancelExport } = useExportDhcs();

	const { data, isLoading } = useGetDhcs({
		page,
		limit: 10,
		...(status ? { status } : {}),
		...(name ? { keywords: name } : {}),
		...(keywords ? { keywords } : {}),
	});
	const { data: dhcList, isError: isDhcListError } = useGetDhcList();

	const handleFilterChange = (filterId: string, value: string) => {
		setFilterValue(filterId, value);
		setPage(1);
	};

	const handleClearFilter = (filterId: string) => {
		clearFilter(filterId);
		setPage(1);
	};

	const handleClearAllFilters = () => {
		clearAllFilters();
		setPage(1);
	};

	const formatSurveyScore = (item: Dhc) => {
		if (item?.surveyAverage) {
			const average = parseNumber(item.surveyAverage);
			if (average !== undefined) {
				return `${average}/10`;
			}
			return "";
		}
		return "";
	};

	const formatActivities = (item: Dhc) => {
		if (typeof item.lessThan3Activities7 === "number" && typeof item.lessThan3Activities30 === "number") {
			return `${item.lessThan3Activities7}/${item.lessThan3Activities30}`;
		}
		return "";
	};

	const mapDhcRow = (item: Dhc, index: number) => ({
		id: item.id ?? item.userId ?? index + 1,
		name: item.name,
		avatarUrl: item.userPhoto || item.avatarUrl,
		clients: item.clients,
		surveyScore: formatSurveyScore(item),
		payout: item.payout,
		score: parseNumber(item.surveyAverage) ?? parseNumber(item.surveyScore) ?? 0,
		surveyLow: item.surveyLow,
		clientWorkouts: item.averageWorkouts ?? undefined,
		clientWorkoutsPercentage: item.averageWorkoutsCompletion ?? undefined,
		payoutStatus: item.payoutStatus,
		activities: formatActivities(item),
		churn:
			item?.churn30Days && item?.churnAllTime && item?.churn90Days
				? `${item.churn30Days}/${item.churn90Days}/${item.churnAllTime}`
				: "",
	});

	const dhcRows = data?.result?.length ? data.result.map(mapDhcRow) : [];
	const dhcNameItems =
		dhcList?.result?.map((item) => {
			return {
				id: item.name,
				label: item.name,
			};
		}) ?? [];
	const mappedFilters = filters.map((filter) => {
		if (filter.id === "name") {
			const items = dhcNameItems.length ? dhcNameItems : filter.items;
			return { ...filter, items };
		}
		return filter;
	});
	const showSpecialFilters = getEnvVar("appShowSpecialFilters") === "true";
	const translatedFilters = mappedFilters
		.filter((filter) => filter.id !== "keywords" || showSpecialFilters)
		.map((filter) => ({
			...filter,
			name: t(filter.name, { ns: "common" }),
			description: t(filter.description),
			items:
				filter.id === "name" || filter.id === "keywords"
					? filter.items
					: filter.items.map((item) => ({ ...item, label: t(item.label) })),
		}));
	const tableRows = isDhcListError ? [] : dhcRows;
	const tableLoading = isDhcListError ? false : isLoading;
	const tablePages = isDhcListError ? 0 : (data?.pages ?? 0);
	const tableTotal = isDhcListError ? 0 : (data?.total ?? 0);

	const handleSelectionChange = (selectedIds: Array<number | string>) => {
		setSelectedRows(selectedIds);
		const selectedDhcs = data?.result?.filter((dhc) => selectedIds.includes(dhc.id ?? dhc.userId ?? ""));
		const mapDhcToProfile = (dhc: Dhc): DhcProfile => ({
			dhcId: dhc.userId?.toString() ?? "",
			name: dhc.name,
			email: dhc.email ?? "",
			role: "DHC",
			imageUrl: dhc.userPhoto || dhc.avatarUrl || "",
		});

		const selectedDhcsInfo = selectedDhcs?.map(mapDhcToProfile) ?? [];
		setDhcsInfo(selectedDhcsInfo);

		if (clearSelection) {
			setClearSelection(false);
		}
	};

	const handleDeselect = () => {
		setSelectedRows([]);
		setDhcsInfo([]);
		setClearSelection(true);
	};

	const handleExportFormat = (format: "csv" | "xlsx") => {
		setOpenExportDropdown(false);
		handleExport(format, selectedRows);
	};

	useEffect(() => {
		if (!openApprovePayoutModal) {
			handleDeselect();
		}
	}, [openApprovePayoutModal]);

	return (
		<Container>
			{exportFormat && <GlobalExportLoader format={exportFormat} onCancel={handleCancelExport} />}

			<Flex flexWrap="wrap" mediaMd="gap: 10px;" margin={"20px 0 8px 0"} justifyContent={"space-between"}>
				<Filters
					filters={translatedFilters}
					filterValues={filterValues}
					addTranslation={false}
					onFilterChange={handleFilterChange}
					onClearFilter={handleClearFilter}
					onClearAll={handleClearAllFilters}
					onMoreFilters={() => {}}
					showMoreFilters={false}
				/>

				{selectedRows.length > 0 ? (
					<Flex gap="8px">
						<Button
							borderSize={"1px"}
							customStyles={{ padding: "8px" }}
							filled={false}
							noBackground
							noBorder
							onClick={handleDeselect}>
							<Typography
								color={ForegroundColors.Secondary}
								fontSize="14px"
								fontWeight={500}
								lineHeight="18px"
								fontFamily="Inter-SemiBold">
								{tCommon("deselect")} ({selectedRows.length})
							</Typography>
						</Button>

						<PermissionGuard permission={PermissionEnum.DhcCreate}>
							<Dropdown
								setOpenDropDown={setOpenExportDropdown}
								openDropDown={openExportDropdown}
								position="bottom"
								dropdownContainerStyle={{ width: "auto" }}
								customDropDownContent={
									<ul style={{ padding: "4px 0", minWidth: "120px" }}>
										<ExportItem onClick={() => handleExportFormat("csv")}>
											<Typography
												lineHeight="20px"
												color={TextColors.Secondary}
												fontWeight={500}
												fontFamily={FontFamilies.InterMedium}>
												CSV
											</Typography>
										</ExportItem>
										<ExportItem onClick={() => handleExportFormat("xlsx")}>
											<Typography
												lineHeight="20px"
												color={TextColors.Secondary}
												fontWeight={500}
												fontFamily={FontFamilies.InterMedium}>
												XLSX
											</Typography>
										</ExportItem>
									</ul>
								}
								triggerComponent={
									<Button
										borderSize={"1px"}
										borderRadius={`${BorderRadius.MD}px`}
										iconBefore={<Export width="16px" height={"16px"} />}
										boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
										size={ButtonSize.SMALL}
										borderColor={BorderColors.Primary}
										onClick={() => setOpenExportDropdown(true)}>
										<Typography
											margin="0 0 0 4px"
											color={ForegroundColors.Secondary}
											fontSize="14px"
											fontWeight={600}
											lineHeight="18px"
											fontFamily="Inter-SemiBold">
											{t("export", { ns: "common" })}
										</Typography>
									</Button>
								}
							/>
						</PermissionGuard>

						<Button
							borderSize={"1px"}
							borderRadius={`${BorderRadius.MD}px`}
							iconBefore={<ApprovePayout width="16px" height={"16px"} />}
							boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
							size={ButtonSize.SMALL}
							onClick={() => setOpenApprovePayoutModal(true)}
							borderColor={BorderColors.Primary}>
							<Typography
								margin="0 0 0 4px"
								color={ForegroundColors.Secondary}
								fontSize="14px"
								fontWeight={600}
								lineHeight="18px"
								fontFamily="Inter-SemiBold">
								{t("approve_payout")}
							</Typography>
						</Button>
					</Flex>
				) : null}
			</Flex>

			<DhcsTable
				rows={tableRows}
				onSelectionChange={handleSelectionChange}
				clearSelection={clearSelection}
				loading={tableLoading}
				totalPages={tablePages}
				totalItems={tableTotal}
				currentPage={page}
				onPageChange={setPage}
			/>
			<DeleteDhcModal open={openDeleteModal} setOpen={setOpenDeleteModal} />
			{dhcsInfo.length > 0 && (
				<ApprovePayoutModal dhcs={dhcsInfo} open={openApprovePayoutModal} setOpen={setOpenApprovePayoutModal} />
			)}
		</Container>
	);
};

export { Content };
